import { addMilliseconds, differenceInDays, isAfter, isBefore, subDays } from 'date-fns';

import {
  CompletionConditionType,
  CompletionReward,
  FeeVoucherReward,
  MissionStatus,
  MissionStatusProgressType,
  SpeedBonusProgress,
  ValidityConditionType,
} from '@flipster/apis/v1/rewardHub/types';
import { timestampToDate } from '@flipster/utils/datetime';

import { ActionButtonMissionType, ProgressBarItemProgressType } from '../types';

export const MarginBonusCurrency = 'USDT Bonus';
export const FeeVoucherCurrency = 'USDT Fee voucher';

type LocalizationTable = Record<string, string> | undefined;

export const getLocalizedText = (table: LocalizationTable, locale: string): string | undefined => {
  return table?.[locale] ?? table?.[''];
};

export const isProgressBarType = (item: MissionStatusProgressType): item is ProgressBarItemProgressType => {
  return item.type === 'DepositAmount' || item.type === 'TradingVolume';
};

export const isActionButtonType = (type: MissionStatusProgressType['type']): type is ActionButtonMissionType => {
  return type !== 'SpeedBonus';
};

export const isSpeedBonusProgress = (progress: MissionStatusProgressType): progress is SpeedBonusProgress => {
  return progress.type === 'SpeedBonus';
};

export const getActionButtonMissionType = (
  progress: MissionStatusProgressType,
): ActionButtonMissionType | undefined => {
  const type = progress.type;
  if (!isActionButtonType(type)) return undefined;
  return type;
};

export const isMarginBonusReward = (reward: CompletionReward): reward is { marginBonus: string } => {
  return typeof reward === 'object' && 'marginBonus' in reward;
};

export const isCashReward = (reward: CompletionReward): reward is { cash: [string, string] } => {
  return typeof reward === 'object' && 'cash' in reward;
};

export const isFeeVoucherReward = (reward: CompletionReward): reward is { voucherId: number } => {
  return reward !== null && typeof reward === 'object' && 'voucherId' in reward;
};

export const getRewardType = (completionReward: CompletionReward): 'marginBonus' | 'cash' | 'feeVoucher' | null => {
  const hasFeeVoucher = isFeeVoucherReward(completionReward);
  const hasMarginBonus = isMarginBonusReward(completionReward);
  const hasCash = isCashReward(completionReward);

  if (hasFeeVoucher) return 'feeVoucher';
  if (hasCash) return 'cash';
  if (hasMarginBonus) return 'marginBonus';
  return null;
};

export const getRewardCurrency = (completionReward: CompletionReward): string | null => {
  if (isFeeVoucherReward(completionReward)) return FeeVoucherCurrency;
  if (isMarginBonusReward(completionReward)) return MarginBonusCurrency;
  if (isCashReward(completionReward)) return completionReward.cash[0];
  return null;
};

export const getRewardAmount = (
  completionReward: CompletionReward,
  feeVoucherRewardRecord?: Record<number, FeeVoucherReward>,
): string => {
  if (isFeeVoucherReward(completionReward)) {
    const voucher = feeVoucherRewardRecord?.[completionReward.voucherId];
    return voucher?.amountLimit ?? '0';
  }
  if (isMarginBonusReward(completionReward)) return completionReward.marginBonus;
  if (isCashReward(completionReward)) return completionReward.cash[1];
  return '0';
};

export const getMissionStatusFlags = (status?: Omit<MissionStatus, 'progress'>) => ({
  isAssigned: status?.itemStatus === 'ASSIGNED',
  isExpired: status?.validity === 'EXPIRED',
  isClaimed: status?.itemStatus === 'CLAIMED',
  isSettled: status?.itemStatus === 'SETTLED',
  isRejected: status?.itemStatus === 'REJECTED',
  isComplete: Boolean(status?.isComplete),
  isValid: status?.validity === 'VALID',
  isClaimable: status && Boolean(status.isComplete && status.validity === 'VALID' && status.itemStatus === 'ASSIGNED'),
});

/** 마일스톤 인덱스별 코인 tier (1=small, 2=medium, 3=large). p = i/(n-1), tier: p≤0.25→1, p≤0.75→2, else 3 */
export const getMilestoneTiers = (total: number): (1 | 2 | 3)[] => {
  if (total <= 0) return [];
  if (total === 1) return [1];
  return Array.from({ length: total }, (_, i) => {
    const p = i / (total - 1);
    return p <= 0.25 ? 1 : p <= 0.75 ? 2 : 3;
  });
};

export const getIsDepositAmountCondition = (condition: CompletionConditionType): boolean =>
  typeof condition === 'object' && 'depositAmount' in condition;

export const getIsTradingVolumeCondition = (condition: CompletionConditionType): boolean =>
  typeof condition === 'object' && 'tradingVolume' in condition;

export const getIsTradingVolumeMission = (completionConditions?: CompletionConditionType[]): boolean =>
  Boolean(completionConditions?.some(getIsTradingVolumeCondition));

export const formatBoostMultiplier = (multiplier: string): string => {
  const parsed = Number(multiplier);
  return Number.isFinite(parsed) ? String(parsed) : '1';
};

export const isDateRangeCondition = (
  condition: ValidityConditionType,
): condition is { dateRange: { start: string; end: string } } =>
  typeof condition === 'object' && 'dateRange' in condition;

export const isDurationSinceSignUpCondition = (
  condition: ValidityConditionType,
): condition is { durationSinceSignUp: string } => typeof condition === 'object' && 'durationSinceSignUp' in condition;

export const getDaysUntilExpiration = (conditions: ValidityConditionType[], signupAt?: string): number | undefined => {
  const now = Date.now();

  const getDaysFromCondition = (condition: ValidityConditionType): number | undefined => {
    if (isDateRangeCondition(condition)) {
      const { start, end } = condition.dateRange;
      if (!start || !end) return undefined;

      const startTime = timestampToDate(start);
      const endTime = timestampToDate(end);

      if (isBefore(now, startTime) || isAfter(now, endTime)) return undefined;
      return differenceInDays(endTime, now) + 1;
    }

    if (isDurationSinceSignUpCondition(condition)) {
      const duration = condition.durationSinceSignUp;
      if (!duration) return undefined;

      const signupAtTime = signupAt ? timestampToDate(signupAt) : subDays(new Date(), 1);
      const durationTime = Number(duration) / 1_000_000;
      const endTime = addMilliseconds(signupAtTime, durationTime);

      if (isBefore(now, signupAtTime) || isAfter(now, endTime)) return undefined;
      return differenceInDays(endTime, now) + 1;
    }

    return undefined;
  };

  return conditions.map(getDaysFromCondition).find((days) => days !== undefined);
};

export const getProgressParams = (progress: ProgressBarItemProgressType) => {
  if (progress.type == 'DepositAmount')
    return {
      target: progress.targetAmount,
      current: progress.currentAmount,
    };

  return {
    target: progress.targetVolume,
    current: progress.currentVolume,
  };
};
